/**
 * T&A Admin shell — mirrors employee PeopleHrShell: domain hubs first
 * (Attendance / Leave / Time / Admin Setup), then tool tabs inside each hub.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Airplane,
  CalendarBlank,
  Clock,
  GearSix,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { PageAtmosphere } from "../pages/peopleHr/Scorecard";
import { fetchApprovalsPendingCount } from "../pages/peopleHr/myWorkApi";
import {
  canAccessAttendanceHub,
  canAccessLeaveHub,
  canAccessSetupHub,
  canAccessTaTab,
  canAccessTimeHub,
  hasAnyReportsAccess,
} from "../utils/taAdminAccess";

/** Sidebar / hub match paths — keep in sync with sidebarNav People Admin. */
export const TA_ADMIN_HUB_MATCH = {
  attendance: ["/people/timesheet/admin/attendance"],
  leave: [
    "/people/timesheet/admin/balances",
    "/people/timesheet/admin/approvals",
    "/people/timesheet/admin/leave",
  ],
  time: [
    "/people/timesheet/admin/timesheets",
    "/people/timesheet/admin/control",
    "/people/timesheet/admin/reports",
    "/people/timesheet/admin/follow-up-emails",
    "/people/timesheet/charge-codes",
    "/people/timesheet/clerk-entry",
    "/people/timesheet/reconciliation",
    "/people/timesheet/compliance",
  ],
  setup: [
    "/people/timesheet/policies",
    "/people/timesheet/holidays",
    "/people/timesheet/admin/audit",
    "/people/timesheet/admin/approver-delegations",
    "/people/timesheet/zoho-leave",
  ],
};

const HUBS = {
  attendance: {
    id: "attendance",
    title: "Attendance",
    purpose: "Who is in, home, TDY / training, or on leave.",
    Icon: CalendarBlank,
    home: "/people/timesheet/admin/attendance",
    canAccess: canAccessAttendanceHub,
    tabs: [
      {
        to: "/people/timesheet/admin/attendance",
        label: "Roster",
        end: true,
      },
    ],
  },
  leave: {
    id: "leave",
    title: "Leave",
    purpose: "Balances, approvals, and leave on behalf.",
    Icon: Airplane,
    home: "/people/timesheet/admin/balances",
    canAccess: canAccessLeaveHub,
    tabs: [
      {
        to: "/people/timesheet/admin/balances",
        label: "Balances",
        end: true,
      },
      {
        to: "/people/timesheet/admin/approvals",
        label: "Approvals",
        end: true,
      },
      {
        to: "/people/timesheet/admin/leave",
        label: "Requests",
        end: true,
      },
    ],
  },
  time: {
    id: "time",
    title: "Time",
    purpose: "Timesheet queue, control, follow-up, reports, and charge codes.",
    Icon: Clock,
    home: "/people/timesheet/admin/timesheets",
    canAccess: canAccessTimeHub,
    tabs: [
      {
        to: "/people/timesheet/admin/timesheets",
        label: "Timesheets",
        end: true,
        permission: ["timekeeping.admin", "admin.settings"],
      },
      {
        to: "/people/timesheet/admin/control",
        label: "Timesheet Control",
        end: true,
        permission: ["timekeeping.admin", "admin.settings"],
      },
      {
        to: "/people/timesheet/admin/follow-up-emails",
        label: "Timesheet Follow Up",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.setup.admin",
        ],
      },
      {
        to: "/people/timesheet/admin/reports",
        label: "Reports",
        end: true,
        canAccess: hasAnyReportsAccess,
      },
      {
        to: "/people/timesheet/charge-codes",
        label: "Charge Codes",
        end: true,
        permission: ["timekeeping.admin", "admin.settings", "pto.admin"],
      },
      {
        to: "/people/timesheet/clerk-entry",
        label: "Clerk Entry",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.clerk_entry.initiate",
        ],
      },
      {
        to: "/people/timesheet/reconciliation",
        label: "Reconciliation",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.reconciliation.manage",
        ],
      },
    ],
  },
  setup: {
    id: "setup",
    title: "Admin Setup",
    purpose: "Policies, holidays, audit, delegations, and Zoho sync.",
    Icon: GearSix,
    home: "/people/timesheet/policies",
    canAccess: canAccessSetupHub,
    tabs: [
      {
        to: "/people/timesheet/policies",
        label: "PTO Policies",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.setup.admin",
          "pto.policy.manage",
        ],
      },
      {
        to: "/people/timesheet/admin/country-leaves",
        label: "Country Leaves",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.setup.admin",
          "pto.policy.manage",
        ],
      },
      {
        to: "/people/timesheet/holidays",
        label: "Holidays",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.setup.admin",
        ],
      },
      {
        to: "/people/timesheet/admin/audit",
        label: "Audit Trail",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.setup.admin",
          "timekeeping.reports",
        ],
      },
      {
        to: "/people/timesheet/admin/approver-delegations",
        label: "Approver Delegations",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.setup.admin",
        ],
      },
      {
        to: "/people/timesheet/zoho-leave",
        label: "Zoho Sync",
        end: true,
        permission: [
          "timekeeping.admin",
          "admin.settings",
          "timekeeping.setup.admin",
        ],
      },
    ],
  },
};

const HUB_ORDER = ["attendance", "leave", "time", "setup"];

function hubIdFromPath(pathname) {
  for (const id of HUB_ORDER) {
    if (TA_ADMIN_HUB_MATCH[id].some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return id;
    }
  }
  return null;
}

const tabClass = (active) =>
  `group relative inline-flex min-h-11 items-center gap-2 whitespace-nowrap px-4 py-3 text-sm font-medium border-b-2 cursor-pointer touch-manipulation motion-safe:transition-[color,border-color] motion-safe:duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-900 rounded-t-md ${
    active
      ? "border-brand-blue text-brand-blue dark:border-blue-400 dark:text-blue-400"
      : "border-transparent text-neutral-500 dark:text-zinc-400 hover:text-brand-navy dark:hover:text-zinc-100 hover:border-neutral-300 dark:hover:border-zinc-600"
  }`;

export default function TaAdminShell({ hub, children }) {
  const location = useLocation();
  const { hasPermission, isAdmin } = useAuth();
  const isGlobalAdmin = hasPermission("admin.settings");
  const canViewOrgApprovals =
    hasPermission("pto.admin") ||
    hasPermission("timekeeping.admin") ||
    hasPermission("admin.settings") ||
    isAdmin?.();
  const [leavePending, setLeavePending] = useState(0);
  const [leaveManagerPending, setLeaveManagerPending] = useState(0);
  const [timesheetManagerPending, setTimesheetManagerPending] = useState(0);
  const activeHubId = hubIdFromPath(location.pathname) || hub || "leave";
  const config = HUBS[activeHubId] || HUBS.leave;
  const HubIcon = config.Icon;

  const refreshOrgApprovals = useCallback(async () => {
    if (!canViewOrgApprovals) return;
    const result = await fetchApprovalsPendingCount("org");
    if (result.ok) {
      // Leave hub badge = HR-stage only. Manager-stage + timesheets are visual /
      // on-behalf and do not use the red mandatory badge.
      setLeavePending(result.leave || 0);
      setLeaveManagerPending(result.leaveManager || 0);
      setTimesheetManagerPending(result.timesheetsManager || 0);
    }
  }, [canViewOrgApprovals]);

  useEffect(() => {
    if (!canViewOrgApprovals) return undefined;
    refreshOrgApprovals();
    const iv = setInterval(refreshOrgApprovals, 30000);
    return () => clearInterval(iv);
  }, [canViewOrgApprovals, refreshOrgApprovals]);

  const visibleTabs = useMemo(
    () =>
      (config.tabs || []).filter((t) => {
        if (t.requireGlobalAdmin && !isGlobalAdmin) return false;
        return canAccessTaTab(hasPermission, t);
      }),
    [config.tabs, isGlobalAdmin, hasPermission]
  );
  const showToolTabs = visibleTabs.length > 1;

  const hubLinks = useMemo(
    () =>
      HUB_ORDER.map((id) => {
        const h = HUBS[id];
        if (typeof h.canAccess === "function" && !h.canAccess(hasPermission)) {
          return null;
        }
        const firstTab = (h.tabs || []).find((t) => canAccessTaTab(hasPermission, t));
        return {
          id,
          to: firstTab?.to || h.home,
          label: h.title,
          Icon: h.Icon,
          match: TA_ADMIN_HUB_MATCH[id],
          // Red = HR must act. Time hub never uses red (manager-owned queue).
          badge: id === "leave" ? leavePending : 0,
          hint:
            id === "leave"
              ? leaveManagerPending
              : id === "time"
                ? timesheetManagerPending
                : 0,
        };
      }).filter(Boolean),
    [leavePending, leaveManagerPending, timesheetManagerPending, hasPermission]
  );

  return (
    <PageAtmosphere>
      <a
        href="#ta-admin-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:rounded-lg focus:bg-white dark:focus:bg-zinc-800 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-brand-navy dark:focus:text-zinc-100 focus:shadow-lg focus:ring-2 focus:ring-brand-blue"
      >
        Skip to admin content
      </a>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex flex-col min-h-[calc(100vh-4rem)]">
        <header className="mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="hidden sm:flex p-2.5 rounded-xl bg-brand-navy/10 text-brand-navy dark:bg-blue-500/20 dark:text-blue-300 shrink-0"
              aria-hidden="true"
            >
              <HubIcon size={22} weight="bold" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-semibold leading-tight tracking-tight text-brand-navy dark:text-zinc-50 text-pretty">
                {config.title}
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-1.5 max-w-2xl leading-relaxed">
                {config.purpose}
              </p>
            </div>
          </div>
        </header>

        <div className="mb-1 border-b border-neutral-200 dark:border-zinc-700">
          <nav
            role="tablist"
            aria-label="T&A Admin domains"
            className="-mb-px flex gap-1 overflow-x-auto touch-pan-x"
          >
            {hubLinks.map((h) => {
              const active = h.match.some(
                (p) =>
                  location.pathname === p || location.pathname.startsWith(`${p}/`)
              );
              const Icon = h.Icon;
              const showBadge = (h.badge || 0) > 0;
              const showHint = (h.hint || 0) > 0;
              return (
                <Link
                  key={h.id}
                  to={h.to}
                  role="tab"
                  aria-selected={active}
                  aria-current={active ? "page" : undefined}
                  className={tabClass(active)}
                >
                  <Icon
                    size={18}
                    weight={active ? "fill" : "regular"}
                    className="shrink-0"
                    aria-hidden="true"
                  />
                  <span>{h.label}</span>
                  {showBadge ? (
                    <span
                      className="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white tabular-nums"
                      aria-label={`${h.badge} awaiting HR`}
                    >
                      {h.badge > 99 ? "99+" : h.badge}
                    </span>
                  ) : null}
                  {showHint ? (
                    <span
                      className="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-900 tabular-nums dark:bg-amber-950/50 dark:text-amber-100"
                      aria-label={`${h.hint} awaiting manager`}
                      title="Awaiting manager — HR can act on behalf"
                    >
                      {h.hint > 99 ? "99+" : h.hint}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </nav>
        </div>

        {showToolTabs ? (
          <div className="mb-5 border-b border-neutral-200/80 dark:border-zinc-700/80 bg-white/60 dark:bg-zinc-900/40 -mx-4 sm:-mx-6 px-4 sm:px-6">
            <nav
              role="tablist"
              aria-label={`${config.title} tools`}
              className="-mb-px flex gap-0.5 overflow-x-auto touch-pan-x"
            >
              {visibleTabs.map((tab) => {
                // Red badge = HR must act. Manager-stage / timesheet queue is informational only.
                const tabBadge =
                  tab.to === "/people/timesheet/admin/approvals"
                    ? leavePending
                    : 0;
                const tabHint =
                  tab.to === "/people/timesheet/admin/approvals" &&
                  leaveManagerPending > 0
                    ? leaveManagerPending
                    : tab.to === "/people/timesheet/admin/timesheets" &&
                        timesheetManagerPending > 0
                      ? timesheetManagerPending
                      : 0;
                return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  role="tab"
                  className={({ isActive }) =>
                    `inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap px-3.5 py-2.5 text-sm font-medium border-b-2 cursor-pointer touch-manipulation motion-safe:transition-[color,border-color] motion-safe:duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/40 rounded-t-md ${
                      isActive
                        ? "border-brand-navy text-brand-navy dark:border-blue-400 dark:text-blue-400"
                        : "border-transparent text-neutral-500 dark:text-zinc-400 hover:text-neutral-800 dark:hover:text-zinc-200"
                    }`
                  }
                >
                  {tab.label}
                  {tabBadge > 0 ? (
                    <span
                      className="inline-flex min-w-[1.15rem] h-[1.15rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white tabular-nums"
                      aria-label={`${tabBadge} awaiting HR`}
                    >
                      {tabBadge > 99 ? "99+" : tabBadge}
                    </span>
                  ) : null}
                  {tabHint > 0 ? (
                    <span
                      className="inline-flex min-w-[1.15rem] h-[1.15rem] items-center justify-center rounded-full bg-amber-100 px-1 text-[10px] font-semibold text-amber-900 tabular-nums dark:bg-amber-950/50 dark:text-amber-100"
                      aria-label={`${tabHint} awaiting manager (on behalf)`}
                      title="Awaiting manager — HR can act on behalf"
                    >
                      {tabHint > 99 ? "99+" : tabHint}
                    </span>
                  ) : null}
                </NavLink>
                );
              })}
            </nav>
          </div>
        ) : (
          <div className="mb-5" />
        )}

        <main id="ta-admin-main" className="flex-1 min-h-0">
          {children}
        </main>
      </div>
    </PageAtmosphere>
  );
}
